'use client'

import { useLocale } from 'next-intl'
import { useEffect, useState } from 'react'

import { TRoomResponse } from '@/entities/room/api/get-room/schema'
import { MultiScrollProvider } from '@/shared/lib'
import { Image, Separator, Spinner } from '@/shared/ui'

import EnglishSuperHostBadge from '../../asset/super-host-badge-en.png'
import SuperHostBadge from '../../asset/super-host-badge.png'
import { AirbridgeEventSender } from '../../lib/airbridge-event-sender'
import { convertSimpleReviewCounts } from '../../lib/review'
import { convertToRoomDetailProps } from '../../model/converter'
import { MobileRoomImageCarousel } from '../carousel'
import { MobileContractActionArea } from '../contract-action-area'
import { MobileContractFee } from '../contract-fee'
import { MobileContractInformation } from '../contract-information'
import { DiscountInformation } from '../discount-information'
import { MobileRoomHeader } from '../header'
import { MobileHostIntroduction } from '../host-introduction'
import { MobileRefundTerm } from '../refund-term'
import { MobileReview } from '../review'
import { MobileRoomInformation } from '../room-information'
import { ScrollAwareHeader } from '../scroll-aware-header'
import { MobileTabs } from '../tabs'

type Props = {
  roomId: number
  roomResponse: TRoomResponse
}

export const MobileRoomDetail = ({ roomId, roomResponse }: Props) => {
  const locale = useLocale()

  const [isMounted, setIsMounted] = useState(false)

  const isEnglish = locale === 'en'

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return <Spinner />
  }

  const {
    hostUser: { isSuperHost },
    airbridgeEvent,
  } = roomResponse

  const room = convertToRoomDetailProps(roomResponse)
  const { hostSimpleReview, roomSimpleReview } = convertSimpleReviewCounts(
    room.simpleReviewCounts,
  )

  return (
    <div className="flex grow flex-col pb-35">
      <AirbridgeEventSender airbridgeEvent={airbridgeEvent} />
      <ScrollAwareHeader roomName={room.roomName} />
      <MobileRoomImageCarousel
        images={room.pictures}
        roomName={room.roomName}
        roomUrl={room.roomUrl}
        className="max-w-[100vw]"
      />
      <section className="relative space-y-4 px-4 pt-6 pb-8">
        {isSuperHost && (
          <Image
            src={isEnglish ? EnglishSuperHostBadge : SuperHostBadge}
            alt="Super Host Badge"
            width={80}
            height={80}
            className="absolute -top-[26px] right-4"
            external={false}
          />
        )}
        <MobileRoomHeader
          roomName={room.roomName}
          address={room.addrStreet}
          isSuperHost={isSuperHost}
        />
        <MobileContractFee
          usingFee={room.usingFee}
          mgmtFee={room.mgmtFee}
          minimumContractWeeks={room.minimumContractWeeks}
        />
        <DiscountInformation
          longTermDiscounts={room.longTermDiscounts}
          earlyCheckinDiscounts={room.earlyCheckinDiscounts}
          showSeparator
          className="px-0"
        />
      </section>
      <Separator color="light-gray" size="large" />
      <MultiScrollProvider>
        <MobileTabs />
        <MobileRoomInformation {...room} />
        <MobileContractInformation
          usingFee={room.usingFee}
          mgmtFee={room.mgmtFee}
          cleanFee={room.cleanFee}
          deposit={room.deposit}
          earlyCheckinDiscounts={room.earlyCheckinDiscounts}
          longTermDiscounts={room.longTermDiscounts}
        />
        <MobileReview
          reviewScore={room.reviewScore}
          reviewList={room.reviewList}
          roomSimpleReview={roomSimpleReview}
        />
        <Separator color="light-gray" size="large" />
        <MobileHostIntroduction
          rid={room.rid}
          hostUser={room.hostUser}
          otherHostRooms={room.otherHostRooms}
          hostSimpleReview={hostSimpleReview}
        />
        <Separator color="light-gray" size="large" />
        <MobileRefundTerm refundTerms={room.refundTerms} />
      </MultiScrollProvider>
      <MobileContractActionArea roomId={roomId} />
    </div>
  )
}
